// Shared inline-style object. Verbatim extract from main.jsx.

// Every member is a React inline-style object, so `CSSProperties` is the honest
// description. It is applied member-by-member with `satisfies` rather than as
// one declared type on `S`, for two reasons:
//
//   * `satisfies` still CHECKS each object against CSSProperties but leaves the
//     literal types in place, so `S.header.position` stays 'sticky' instead of
//     widening to `string` — which is what makes these objects assignable to a
//     `style=` prop from a TypeScript component at all.
//   * tests/static/design_token_contract_static.test.js finds each shared button
//     token by searching this file for the token's NAME and reading the next 260
//     characters for its padding and radius. A single annotation block would put
//     every member name above the object literal, so that search would land on
//     the annotation and read type text instead of the style. Annotating in
//     place keeps the first occurrence of each name on the style it describes —
//     and is why no token name is spelled out in this comment either.
import type {CSSProperties} from 'react';

import {buttonDangerText, buttonPrimary, buttonSecondary} from './buttonStyles.js';

export const S = {
  header: {
    // Homepage-redesign "header-green" gradient (modern-themes.css .header-green):
    // 102deg, two-stop oklch greens. The shipped header was already a green
    // gradient with white text + white 36px icon buttons, so this aligns the
    // exact hue/angle to the design without touching DOM, hooks, or behavior.
    background: 'linear-gradient(102deg, oklch(0.355 0.058 166), oklch(0.435 0.078 170))',
    color: 'white',
    padding: '10px 1.25rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
    position: 'sticky',
    top: 0,
    zIndex: 100,
    boxShadow: '0 2px 8px rgba(0,0,0,.12)',
  } satisfies CSSProperties,
  card: {
    background: 'white',
    border: '1px solid #E6E8EB',
    borderRadius: 10,
    overflow: 'hidden',
  } satisfies CSSProperties,
  label: {fontSize: 12, color: '#5B636D', marginBottom: 3, display: 'block'} satisfies CSSProperties,
  fieldGroup: {display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10} satisfies CSSProperties,
  badge: (bg: string, tx: string): CSSProperties => ({
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 500,
    background: bg,
    color: tx,
  }),
  // Buttons come from the one source in buttonStyles (§4). btnPrimary keeps its
  // historical full width for form footers.
  btnPrimary: {...buttonPrimary, width: '100%'} satisfies CSSProperties,
  btnDanger: {...buttonDangerText} satisfies CSSProperties,
  btnGhost: {...buttonSecondary} satisfies CSSProperties,
};

// Pick dark or light text for any colored background. Threshold 0.5 on
// perceived luminance (YIQ): lighter half gets near-black, darker half
// gets white. Use anywhere program/palette colors back inline text —
// timeline bars, batch tile headers, herd/flock cycle headers, etc.
// `hexBg` is deliberately not narrowed to `string`: the `!hexBg` and
// `String(...)` guards below exist because callers hand this whatever a batch
// or program row happens to carry, and every call site is still JavaScript.
export function getReadableText(hexBg: string | null | undefined): string {
  if (!hexBg) return '#1A1F24';
  const c = String(hexBg).replace('#', '');
  if (c.length !== 6) return '#1A1F24';
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  const L = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return L >= 0.5 ? '#1A1F24' : 'white';
}
